<template>
  <b-row class="additional-traveler-bags w-100">
    <b-col
      lg="9"
      md="12"
      sm="12"
      class="d-flex align-items-center additional-traveler-bags__traveler-label"
    >
      <traveler-label :traveler_info="traveler" :index="traveler_index" />
    </b-col>
    <b-col lg="3" md="12" sm="12">
      <selector
        :selected="selected"
        :options="bag_options"
        :onChange="
          (b) =>
            select_bag(traveler.id, selected === b ? UNSET_BAG_SELECTION : b)
        "
        button_classes="rounded-full"
        showDividers
      >
      </selector>
    </b-col>
    <b-col>
      <hr style="margin-top: 20px; margin-bottom: 20px" />
    </b-col>
  </b-row>
</template>

<script>
import { number } from "mathjs";
import { apply_markup_to_number } from "@liveflight/utilities/price";
import { currency_decimals } from "@resources";
import { decode_bag_info, encode_bag_info } from "./utilities";
import Selector from "@liveflight/components/blocks/selector.vue";
import TravelerLabel from "@liveflight/components/blocks/traveler/traveler-label.vue";

const UNSET_BAG_SELECTION = "-1";

export default {
  name: "additional-traveler-bags",
  components: {
    selector: Selector,
    "traveler-label": TravelerLabel,
  },
  props: {
    traveler: Object,
    traveler_index: Number,
    selected: String,

    flight_offer: Object,
    flexible_bags: Object,
    bags: Object,

    select_bag: Function,
    itinerary_contains_bag: Function,
  },

  data() {
    return {
      UNSET_BAG_SELECTION,
    };
  },
  methods: {
    apply_markup_to_number,
    segments_are_in_itinerary(itinerary_index, segments) {
      if (!segments || segments.length === 0) {
        return false;
      }
      if (itinerary_index < 0 || this.itineraries.length <= itinerary_index) {
        return false;
      }
      return this.itineraries[itinerary_index].segments.every((segment) =>
        segments.includes(segment.id)
      );
    },
  },
  computed: {
    itineraries() {
      return Object.entries(this.flight_offer.itineraries || {}).map(
        (itinerary) => ({
          itinerary_id: itinerary[0],
          ...itinerary[1],
        })
      );
    },
    distinct_bags() {
      const distinct = {};

      for (
        let itinerary_index = 0;
        itinerary_index < this.itineraries.length;
        itinerary_index += 1
      ) {
        const bags = this.flexible_bags[this.traveler_index][itinerary_index];
        const bags_entries = Object.entries(bags);
        for (let index = 0; index < bags_entries.length; index += 1) {
          try {
            const entry = bags_entries[index];
            const bag_id = entry[0];
            const { bag } = entry[1];

            if (
              (bag.quantity || 1) === 1 &&
              this.segments_are_in_itinerary(itinerary_index, bag.segmentIds) &&
              bag.travelerIds.includes(this.traveler.id)
            ) {
              const encoded = encode_bag_info(bag);
              distinct[encoded] = {
                ...(distinct[encoded] || {}),
                [itinerary_index]: { bag_id, ...bag },
              };
            }
          } catch (e) {
            this.log("additional-traveler-bags.distinct_bags", e);
          }
        }
      }
      return Object.entries(distinct);
    },
    bag_options() {
      // Default option
      const options = {
        [UNSET_BAG_SELECTION]: {
          value: UNSET_BAG_SELECTION,
          icon: {
            name: "luggage",
          },
          label: "Aggiungi un bagaglio",
          html: "Nessun bagaglio",
          total_price: 0,
        },
      };

      // Per-render memo: catalog price.total → marked-up number.
      // apply_markup_to_number reaches into atob+JSON.parse — cache it
      // across iterations of the same render.
      const markup_cache = {};
      const { price: offer_price = {} } = this.flight_offer || {};
      const currency_glyph = offer_price.currencySymbol || "";
      const decimals = currency_decimals(offer_price.currency || "");

      for (
        let bag_index = 0;
        bag_index < this.distinct_bags.length;
        bag_index += 1
      ) {
        const entry = this.distinct_bags[bag_index];
        const bags = entry[1];
        const decoded = decode_bag_info(entry[0]);
        const quantity = decoded.quantity;
        const weight = decoded.weight ? `(${decoded.weight})` : "";
        let total_price = 0;
        const option_value = [];
        for (
          let itinerary_index = 0;
          itinerary_index < this.itineraries.length;
          itinerary_index += 1
        ) {
          const itinerary_bag = bags[itinerary_index];
          if (itinerary_bag) {
            const cache_key = itinerary_bag.price.total;
            if (markup_cache[cache_key] === undefined) {
              markup_cache[cache_key] = number(
                this.apply_markup_to_number(this.flight_offer, cache_key),
              );
            }
            total_price += markup_cache[cache_key];
            if (!option_value.includes(String(itinerary_bag.bag_id))) {
              option_value.push(itinerary_bag.bag_id);
            }
          }
        }

        option_value.sort((a, b) => number(a) - number(b));
        const label = `${quantity} bagagl${number(quantity) !== 1 ? "i" : "io"}`;
        options[option_value.join(":")] = {
          value: option_value.join(":"),
          label: `${label} ${weight}`,
          total_price,
          icon: {
            name: "check_circle",
            icon_style: {
              '--color': this.sass("success"),
            }
          },
          html: `<div class="d-flex justify-content-between">
                  <span>${label} ${weight ? `<span class="semibold">${weight}</span>` : ""}</span>
                  <span class="semibold text-primary">${number(total_price).toFixed(decimals)}${currency_glyph}</span>
                </div>`,
        };
      }

      return Object.values(options).sort(
        (a, b) => a.total_price - b.total_price
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.additional-traveler-bags {
  &__traveler-label {
    @include media-breakpoint-down(md) {
      margin-bottom: 16px;
    }
  }
}
</style>
